import React, { ReactNode } from "react";

import assert from "../../util/assert";
import freeze from "../../util/freeze";
import {
  _RomanChordName as RomanChordName,
  _NoteName as NoteName,
  _PlainNoteName as PlainNoteName,
  _ScaleType as ScaleType,
  _allChordOptions as allChordOptions,
  _romanTriadChordOptions as romanTriadChordOptions,
  _romanSeventhChordOptions as romanSeventhChordOptions,
  Interval,
  intervals,
} from "./MusicTypesAndConstants";

// FIXME importing highest/lowestSupportedNote from SoundLib doesn't work in
// tests. Possibly Babel/Jest issue?
//import { highestSupportedNote, lowestSupportedNote } from "./SoundLib";
import {
  highestSupportedNote as _highestSupportedNote,
  lowestSupportedNote as _lowestSupportedNode,
} from "../../sound-assets/Mp3FileProxy";

const highestSupportedNote = _highestSupportedNote as NoteName;
const lowestSupportedNote = _lowestSupportedNode as NoteName;

export {
  allChordOptions,
  romanTriadChordOptions,
  romanSeventhChordOptions,
  intervals,
};

export type { RomanChordName, NoteName, PlainNoteName, ScaleType, Interval };

export type ChordType = "M" | "m";

export type ChromaticScaleIndex =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13;

export const plainNoteNames: PlainNoteName[] = [
  "C",
  "Db",
  "D",
  "Eb",
  "E",
  "F",
  "Gb",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];

const plainMajorChordNames = [
  "CM",
  "DbM",
  "DM",
  "EbM",
  "EM",
  "FM",
  "GbM",
  "GM",
  "AbM",
  "AM",
  "BbM",
  "BM",
] as const;

const plainMinorChordNames = [
  "Cm",
  "Dbm",
  "Dm",
  "Ebm",
  "Em",
  "Fm",
  "Gbm",
  "Gm",
  "Abm",
  "Am",
  "Bbm",
  "Bm",
] as const;

export const plainChordNames = [
  ...plainMajorChordNames,
  ...plainMinorChordNames,
] as const;

export type PlainChordName = (typeof plainChordNames)[number];

const noteToScaleIndexMap: Record<PlainNoteName, ChromaticScaleIndex> = {
  C: 1,
  Db: 2,
  D: 3,
  Eb: 4,
  E: 5,
  F: 6,
  Gb: 7,
  G: 8,
  Ab: 9,
  A: 10,
  Bb: 11,
  B: 12,
};

const scaleIndexToPlainNoteMap: { [key: number]: PlainNoteName } = {
  1: "C",
  2: "Db",
  3: "D",
  4: "Eb",
  5: "E",
  6: "F",
  7: "Gb",
  8: "G",
  9: "Ab",
  10: "A",
  11: "Bb",
  12: "B",
};

// TODO fix these so that they work in other keys than C, too
function scaleIndexByNote(note: NoteName) {
  return noteToScaleIndexMap[noteToPlainNote(note)];
}

function scaleIndexByPlainNote(plainNote: PlainNoteName) {
  return noteToScaleIndexMap[plainNote];
}

function plainNoteByScaleIndex(scaleIndex: number) {
  return scaleIndexToPlainNoteMap[scaleIndex];
}

/**
 * A0 = 1, C2 = 16, B5 = 63
 * {@link https://www.inspiredacoustics.com/en/MIDI_note_numbers_and_center_frequencies}
 */
export function pianoKeyNumberByNote(note: NoteName) {
  const octaveNumber = octaveOfNote(note);
  assert(() => Number.isInteger(octaveNumber) && !Number.isNaN(octaveNumber));
  const scaleIndex = scaleIndexByNote(note);
  assert(() => scaleIndex >= 1 && scaleIndex <= 12);
  return octaveNumber * 12 + scaleIndex - 9;
}

function octaveOfNote(note: NoteName) {
  return parseInt(note.substr(-1));
}

export function noteToPlainNote(note: NoteName): PlainNoteName {
  return note.replace(/\d/, "") as PlainNoteName;
}

/**
 * A0 = 1, C2 = 16, B6 = 75
 * {@link https://www.inspiredacoustics.com/en/MIDI_note_numbers_and_center_frequencies}
 */
export function noteByPianoKeyNumber(pianoKeyNumber: number): NoteName {
  const scaleIndex = 1 + ((pianoKeyNumber + 8) % 12);
  assert(() => scaleIndex >= 1 && scaleIndex <= 12);
  const octave = (pianoKeyNumber + 9 - scaleIndex) / 12;
  assert(() => Number.isInteger(octave));
  assert(() => octave >= 2 && octave <= 6);
  const plainNote = plainNoteByScaleIndex(scaleIndex);
  return `${plainNote}${octave}` as NoteName;
}

export function noteByScaleIndexAndKey(
  scaleIndex: ChromaticScaleIndex,
  key: NoteName
): NoteName {
  const keyPianoKey = pianoKeyNumberByNote(key);
  const pianoKey = keyPianoKey + scaleIndex - 1;
  return noteByPianoKeyNumber(pianoKey);
}

/**
 * @example
 * getNotesForChord("I", "C3"); // returns ["C2", "E2", "G2"]
 * getNotesForChord("IVmaj7", "C3"); // returns ["F3", "A3", "C4", "E4"]
 */
export function getNotesForChord(chordName: RomanChordName, key: NoteName) {
  // TODO optimize this; cache?
  const chordIntervalMap = freeze<Record<RomanChordName, number[]>>({
    I: [0, 4, 7],
    ii: [2, 5, 9],
    iii: [4, 7, 11],
    IV: [5, 9, 12],
    V: [7, 11, 14],
    vi: [9, 12, 16],
    viio: [11, 14, 17],
    Imaj7: [0, 4, 7, 11],
    iimin7: [2, 5, 9, 12],
    iiimin7: [4, 7, 11, 14],
    IVmaj7: [5, 9, 12, 16],
    V7: [7, 11, 14, 17],
    vimin7: [9, 12, 16, 19],
    vii7b5: [11, 14, 17, 21],
  });

  const chordMap: { [key: string]: NoteName[] } = {};
  for (let chord in chordIntervalMap) {
    const noteIntervals = chordIntervalMap[chord as RomanChordName];
    const notes = noteIntervals.map((i) => modulateNote(key, i));
    chordMap[chord] = notes;
  }

  const notes = chordMap[chordName];
  assert(() => !!notes);
  return notes;
}

/**
 * @example
 * modulateNote("C2", 2); // returns "D2"
 * modulateNote("D5", -10); // return "E4"
 *
 */
export function modulateNote(noteName: NoteName, diff: number) {
  const pianoKeyNumber = pianoKeyNumberByNote(noteName);
  const newPianoKeyNumber = pianoKeyNumber + diff;
  assert(() => newPianoKeyNumber <= pianoKeyNumberByNote(highestSupportedNote));
  assert(() => newPianoKeyNumber >= pianoKeyNumberByNote(lowestSupportedNote));
  return noteByPianoKeyNumber(newPianoKeyNumber);
}

const flatSymbol = "\u266d";
const sharpSymbol = "\u266f";

/**
 * @example
 * noteDisplayName("C4"); // returns "C4"
 * noteDisplayName("Eb4"); // returns "D♯/E♭4"
 *
 */
export function noteDisplayName(noteName: NoteName) {
  const plainNote = noteToPlainNote(noteName);
  const plainNoteDisplay = plainNoteDisplayName(plainNote);
  const octaveNumber = octaveOfNote(noteName);
  return plainNoteDisplay + octaveNumber;
}

export function plainNoteDisplayName(plainNoteName: PlainNoteName) {
  if (["C", "D", "E", "F", "G", "A", "B"].includes(plainNoteName)) {
    return plainNoteName;
  } else {
    switch (plainNoteName) {
      case "Db":
        return "C" + sharpSymbol + "/D" + flatSymbol;
      case "Eb":
        return "D" + sharpSymbol + "/E" + flatSymbol;
      case "Gb":
        return "F" + sharpSymbol + "/G" + flatSymbol;
      case "Ab":
        return "G" + sharpSymbol + "/A" + flatSymbol;
      case "Bb":
      default:
        return "A" + sharpSymbol + "/B" + flatSymbol;
    }
  }
}

const DiminishedSymbol = "\u00B0";

export function chordDisplayName(chordName: RomanChordName): ReactNode {
  switch (chordName) {
    case "viio":
      return `vii${DiminishedSymbol}`;
    case "Imaj7":
    case "iimin7":
    case "iiimin7":
    case "IVmaj7":
    case "V7":
    case "vimin7":
      const base = chordName
        .replace("min", "m")
        .replace("maj", "M")
        .replace("7", "");
      return (
        <>
          {base}
          <sup>{"7"}</sup>
        </>
      );
    case "vii7b5":
      return (
        <>
          {"vii"}
          <sup>{"7b5"}</sup>
        </>
      );
    default:
      return chordName;
  }
}

export function intervalDisplayName(interval: Interval): string {
  return interval === "A4" ? "A4/D5" : interval;
}

export const possibleKeysForChordProgression = (() => {
  const allNotes = [0, 1, 2, 3, 4, 5, 6, 7, 8].reduce<NoteName[]>(
    (all, octave) => {
      const notesInOctave = plainNoteNames
        .map((n) => n + octave)
        .filter(isNoteName);
      return [...all, ...notesInOctave];
    },
    [] as NoteName[]
  );
  const lowestKey = pianoKeyNumberByNote(lowestSupportedNote);
  // TODO Remove magic number 21
  const highestKey = pianoKeyNumberByNote(highestSupportedNote) - 21;
  const possibleNotes = allNotes.filter((n) => {
    const pianoKeyNumber = pianoKeyNumberByNote(n);
    return pianoKeyNumber >= lowestKey && pianoKeyNumber <= highestKey;
  });
  return possibleNotes;
})(); // immediate call

/**
 * Type guard for NoteName.
 *
 * @example
 * ["C2", "Db2", "foo"].filter(isNoteName)
 * // Returns ["C2", "Db2"] of type NoteName[]
 */
function isNoteName(s: string): s is NoteName {
  return /^(C|Db|D|Eb|E|F|Gb|G|Ab|A|Bb|b)\d$/.test(s);
}

export function chordTypeByNotes(notes: PlainNoteName[]): ChordType {
  assert(() => notes.length === 3);
  const [a, b] = notes;
  // Base ocatve could be anything
  const baseOctave = 3;
  const aNoteName = (a + baseOctave) as NoteName;
  const initialBNoteName = (b + baseOctave) as NoteName;
  const bOctave =
    scaleIndexByNote(initialBNoteName) < scaleIndexByNote(aNoteName)
      ? baseOctave + 1
      : baseOctave;
  const bNoteName = (b + bOctave) as NoteName;
  const aPianoKeyNumber = pianoKeyNumberByNote(aNoteName);
  const bPianoKeyNumber = pianoKeyNumberByNote(bNoteName);
  const diff = bPianoKeyNumber - aPianoKeyNumber;
  assert(() => diff === 3 || diff === 4);
  if (diff === 3) {
    return "m";
  } else {
    return "M";
  }
}

export type ScaleAccidentals = "sharps" | "flats";

const rootToAccidentalsMajor: Record<PlainNoteName, ScaleAccidentals> = {
  C: "sharps", // could be either; no accidentals
  Db: "flats",
  D: "sharps",
  Eb: "flats",
  E: "sharps",
  F: "flats",
  Gb: "sharps",
  G: "sharps",
  Ab: "flats",
  A: "sharps",
  Bb: "flats",
  B: "sharps",
};

const rootToAccidentalsMinor: Record<PlainNoteName, ScaleAccidentals> = {
  C: "flats",
  Db: "sharps",
  D: "flats",
  Eb: "sharps",
  E: "sharps",
  F: "flats",
  Gb: "sharps",
  G: "flats",
  Ab: "sharps",
  A: "sharps", // could be either; no accidentals
  Bb: "flats",
  B: "sharps",
};

export function accidentalsByRootAndChordType(
  root: PlainNoteName,
  chordType: ChordType
): ScaleAccidentals {
  assert(() => chordType === "M" || chordType === "m");
  if (chordType === "M") {
    return rootToAccidentalsMajor[root];
  } else {
    return rootToAccidentalsMinor[root];
  }
}

export function plainNoteDisplayNameWithCorrectAccidental(
  note: PlainNoteName,
  rootNote: PlainNoteName,
  chordType: ChordType,
  overrideAccidentals?: ScaleAccidentals
): string {
  const scaleAccidentals =
    overrideAccidentals || accidentalsByRootAndChordType(rootNote, chordType);
  if (note.length === 1 || scaleAccidentals === "flats") {
    return note.replace("b", flatSymbol);
  } else {
    switch (note) {
      case "Db":
        return "C" + sharpSymbol;
      case "Eb":
        return "D" + sharpSymbol;
      case "Gb":
        return "F" + sharpSymbol;
      case "Ab":
        return "G" + sharpSymbol;
      case "Bb":
      default:
        return "A" + sharpSymbol;
    }
  }
}

export function noteDisplayNameWithCorrectAccidental(
  note: NoteName,
  chordType: ChordType
): string {
  const octave = octaveOfNote(note);
  const plainNoteName = plainNoteNameByNoteName(note);
  const plainNoteDisplayName = plainNoteDisplayNameWithCorrectAccidental(
    plainNoteName,
    plainNoteName,
    chordType
  );
  return plainNoteDisplayName + octave;
}

export function plainNoteNameByNoteName(noteName: NoteName): PlainNoteName {
  return noteName.replace(/\d$/, "") as PlainNoteName;
}

export function plainNotesByRootAndChordType(
  root: PlainNoteName,
  chordType: ChordType
): PlainNoteName[] {
  const rootScaleIndex = scaleIndexByPlainNote(root);
  if (chordType === "M") {
    const scaleIndexes = [
      rootScaleIndex,
      1 + ((rootScaleIndex + 3) % 12),
      1 + ((rootScaleIndex + 6) % 12),
    ];
    return scaleIndexes.map(plainNoteByScaleIndex);
  } else {
    const scaleIndexes = [
      rootScaleIndex,
      1 + ((rootScaleIndex + 2) % 12),
      1 + ((rootScaleIndex + 6) % 12),
    ];
    return scaleIndexes.map(plainNoteByScaleIndex);
  }
}

export function plainNotesByPlainChordName(chord: PlainChordName) {
  return plainNotesByRootAndChordType(
    ...rootAndChordTypeByPlainChordName(chord)
  );
}

export function plainChordNameByRootAndChordType(
  root: PlainNoteName,
  chordType: ChordType
): PlainChordName {
  const plainChordName = (root + chordType) as PlainChordName;
  assert(() => plainChordNames.includes(plainChordName));
  return plainChordName;
}

export function rootAndChordTypeByPlainChordName(
  chord: PlainChordName
): [PlainNoteName, ChordType] {
  const root = chord.replace(/[mM]/, "") as PlainNoteName;
  assert(() => plainNoteNames.includes(root));
  const chordType: ChordType = chord.substr(-1) === "M" ? "M" : "m";
  return [root, chordType];
}

/**
 * E = 6, A = 5, D = 4, G = 3, B = 2, e = 1
 */
export type GuitarString = 1 | 2 | 3 | 4 | 5 | 6;

export type Fret = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export type FretboardNote = {
  string: GuitarString;
  fret: Fret;
  plainNote?: PlainNoteName;
  label?: string;
  type?: "default" | "correct";
};

export type FretboardInterval = {
  root: FretboardNote;
  note: FretboardNote;
};

export type FretboardIntervalPattern = {
  rootString: String;
  stringDiff: number;
  fretDiff: number;
};

export function isValidIntervalString(intervalString: string) {
  return /^[1-6](,[1-6]){0,5}\|(0|-?[1-3])\|(0|-?[1-3])\|[mMPA][1-8]$/.test(
    intervalString
  );
}

export function isValidInterval(interval: string): interval is Interval {
  return ([...intervals] as string[]).includes(interval);
}

export function isValidPlainNote(note: string): note is PlainNoteName {
  return (plainNoteNames as string[]).includes(note);
}

export function intervalByFretboardIntervalString(intervalString: string) {
  assert(() => isValidIntervalString(intervalString));
  const match = intervalString.match(/^.*\|(\w{2})$/);
  const interval = (match as string[])[1] as Interval;
  assert(() => intervals.includes(interval));
  return interval;
}

export function parseIntervalString(intervalString: string): {
  strings: GuitarString[];
  stringDiff: number;
  fretDiff: number;
  interval: Interval;
} {
  const regex = /^(\d(,\d)*)\|(-?\d)\|(-?\d)\|(\w{2})$/;
  const match = intervalString.match(regex);

  if (match === null || match.length !== 6) {
    throw new Error(
      `Error when parsing intervalString: ${intervalString}: ${JSON.stringify(
        match
      )}
    `
    );
  }
  assert(() => /^[1-6](,[1-6]){0,5}$/.test(match[1]));
  assert(() => /^-?[0-3]$/.test(match[3]));
  assert(() => /^-?1?[0-9]$/.test(match[4]));
  assert(() => /^[mMPA][1-8]$/.test(match[5]));
  const stringStr = match[1];
  const stringDiffStr = match[3];
  const fretDiffStr = match[4];
  const intervalStr = match[5];
  return {
    strings: stringStr.split(",").map((s) => parseInt(s) as GuitarString),
    stringDiff: parseInt(stringDiffStr),
    fretDiff: parseInt(fretDiffStr),
    interval: intervalStr as Interval,
  };
}

export type ScaleTypeShort = "M" | "m";

export function replaceOctaveInNote(note: NoteName, octave: number): NoteName {
  return note.replace(/\d$/, "" + octave) as NoteName;
}
